export type CaseStatus = 
  | "new" 
  | "meeting_scheduled" 
  | "busy" 
  | "on_hold" 
  | "transferred"
  // Potential future statuses to match columns if needed, 
  // but for now we map existing DB statuses.
  | "stuck" 
  | "review"; 

export type TabId = "active" | "archived" | "trash";
export type ColumnId = "awaiting_meeting" | "upcoming" | "in_progress" | "stuck" | "review";

export interface StatusConfigDef {
  label: string;
  tab: TabId;
  column: ColumnId;
  isStale?: boolean;
}

export const STATUS_MAPPING: Record<string, StatusConfigDef> = {
  new: { 
    label: "New", 
    tab: "active", 
    column: "awaiting_meeting" 
  },
  meeting_scheduled: { 
    label: "Meeting Scheduled", 
    tab: "active", 
    column: "upcoming" 
  },
  busy: { 
    label: "In Progress", 
    tab: "active", 
    column: "in_progress" 
  },
  on_hold: {
    label: "On Hold",
    tab: "active",
    column: "stuck"
  },
  transferred: {
    label: "Archived",
    tab: "archived",
    column: "review"
  },
  // If we ever introduce these as explicit statuses:
  stuck: {
    label: "Stuck / Blocked",
    tab: "active",
    column: "stuck"
  },
  review: {
    label: "Ready for Review",
    tab: "active",
    column: "review"
  }
};

export const getStatusConfig = (status: string): StatusConfigDef => {
  return STATUS_MAPPING[status] || STATUS_MAPPING["new"];
};

export const resolveCaseStatus = (c: any): StatusConfigDef => {
  // Archived cases always stay archived — no derived overrides
  if (c.status === "transferred") return STATUS_MAPPING["transferred"];

  // 2. Logic extraction
  const progress = c.totalItems > 0 ? (c.completedCount || c.progress || 0) / c.totalItems * 100 : 0;
  const isBlocked = c.blockedItems ? Object.values(c.blockedItems).some((v) => v === true) : false;
  
  // Date handling
  let lastUpdated = new Date(0);
  if (c.lastUpdated) {
    if (typeof c.lastUpdated === "object" && "seconds" in c.lastUpdated) {
      lastUpdated = new Date(c.lastUpdated.seconds * 1000);
    } else if (c.lastUpdated instanceof Date) {
      lastUpdated = c.lastUpdated;
    } else if (typeof c.lastUpdated === "string") {
      lastUpdated = new Date(c.lastUpdated);
    }
  }
  const daysInactive = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24);

  let meetingDate = null;
  const meetingObj = c.meeting || {};
  const rawDate = meetingObj.date || meetingObj.meeting_date;
  if (rawDate) {
    if (typeof rawDate === "object" && "seconds" in rawDate) {
      meetingDate = new Date(rawDate.seconds * 1000);
    } else if (typeof rawDate === "number") {
      meetingDate = new Date(rawDate);
    } else {
      meetingDate = new Date(rawDate);
    }
  }
  const hasFutureMeeting = meetingDate && meetingDate > new Date();

  // 3. Priority Overrides (Derived States)
  
  // Review / Handover
  if (progress >= 90) return STATUS_MAPPING["review"];

  // Stuck / Blocked (ONLY explicit blocks)
  if (isBlocked) return STATUS_MAPPING["stuck"];

  // Stale Logic (Inactive > 7 days)
  // Instead of returning "stuck", we return the base status but mark as stale
  let resolvedConfig = getStatusConfig(c.status || "new");

  // Upcoming (Derived) - overrides base status if applicable
  if (hasFutureMeeting && c.status !== "busy") {
    resolvedConfig = STATUS_MAPPING["meeting_scheduled"];
  }

  // Apply Stale Flag
  if (daysInactive > 7 && !hasFutureMeeting && meetingDate) {
    return { ...resolvedConfig, isStale: true };
  }

  return resolvedConfig;
};
